import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui';

interface GuestOrderLookupInfoProps {
  ordererName: string;
  orderNo: string;
}

const CopyRow = ({ label, value, testId, mask }: { label: string; value: string; testId: string; mask?: boolean }) => {
  const { t } = useTranslation('orders');
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // 클립보드가 막힌 환경 — 값이 화면에 그대로 있어 직접 복사할 수 있다.
    }
  };
  return (
    <div className="flex items-center gap-2 text-[13px]">
      <span className="w-16 shrink-0" style={{ color: 'var(--content-muted)' }}>
        {label}
      </span>
      <span
        className="min-w-0 break-all font-medium"
        style={{ color: 'var(--content-strong)' }}
        data-testid={testId}
        {...(mask ? { 'data-sentry-mask': true } : {})}
      >
        {value}
      </span>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => void copy()}
        aria-label={t('orders.detail.guestLookup.copyAria', { label, defaultValue: '{{label}} 복사' })}
      >
        {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
      </Button>
    </div>
  );
};

/**
 * 비회원 주문조회 정보(CS 안내용). 비회원 조회는 주문 비밀번호 없이 "주문자명 + 주문번호"로 본인 확인한다
 * (2026-06-22 정책, BE GuestOrderService). 비밀번호는 저장하지 않으므로 표시 칸이 없다.
 */
export const GuestOrderLookupInfo = ({ ordererName, orderNo }: GuestOrderLookupInfoProps) => {
  const { t } = useTranslation('orders');
  return (
    <div
      className="mb-3 flex flex-col gap-1.5 rounded-md px-3 py-2"
      style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-soft)' }}
      data-testid="guest-order-lookup"
    >
      <p className="text-[13px] font-semibold" style={{ color: 'var(--content-strong)' }}>
        {t('orders.detail.guestLookup.title', '비회원 주문조회 정보')}
      </p>
      <p className="text-[12px]" style={{ color: 'var(--content-default)' }}>
        {t('orders.detail.guestLookup.method', '쇼핑몰 > 비회원 주문조회에서 주문자명 + 주문번호로 조회 (비밀번호 없음)')}
      </p>
      <CopyRow label={t('orders.detail.guestLookup.ordererName', '주문자명')} value={ordererName} testId="guest-lookup-name" mask />
      <CopyRow label={t('orders.detail.guestLookup.orderNo', '주문번호')} value={orderNo} testId="guest-lookup-order-no" />
      <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
        {t('orders.detail.guestLookup.hint', '공백·영문 대소문자는 구분하지 않습니다. 5회 잘못 입력하면 10분간 조회가 잠깁니다.')}
      </p>
    </div>
  );
};
